import translate from './translate';

// export const EMAIL_REGEXP = /^[\w-]([.\w-])*@([\w-]+)\.+[A-Za-z]{2,6}$/;
export const EMAIL_REGEXP = /^([a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*)@([a-zA-Z0-9]+(?:[.-][a-zA-Z0-9]+)*\.[a-z]{2,})$/; // allow sub-domain
export const CZ_PHONE_REGEXP = /^(\+?420)?([0-9]{9})$/;

type ValidationFunc = (value: any) => string | undefined;

export function applyAll(...validations: Array<ValidationFunc>) {
  return (value: any) => {
    const errors = validations.reduce((acc, cur) => {
      const err = cur(value);
      if (err) {
        return err + '-divider-' + acc;
      }
      return acc;
    }, '');

    return errors.length > 0 ? errors : undefined;
  };
}

export const passwordSameAsOld = (source: string) => (target: string) => {
  if (source === target) {
    return translate('error.validation.password_is_same_as_old');
  }

  return;
};

export const isPrimativeEmpty = (value: unknown): boolean => {
  // excluding zero
  return value === 0 ? false : !Boolean(value);
};

// NOTE: Later on we can add a check for min/max quantity of letters and numbers
export const hasAlphanumericChars = (subject?: string) => (value?: string) => {
  const IS_CHAR = /[À-ža-zA-Z]/g;
  const IS_NUMERIC = /[0-9]/g;

  if (
    value &&
    value.match(IS_CHAR)?.length &&
    value.match(IS_NUMERIC)?.length
  ) {
    return;
  }

  return subject
    ? translate('error.validation.has_alphanumeric_chars_with_subject', {
        subject,
      })
    : translate('error.validation.has_alphanumeric_chars');
};

export const hasSpaces = (value?: string | null): string | undefined => {
  // Password must not contain any kind of whitespaces
  const HAS_SPACES_REGEX = /\s/;

  if (value && HAS_SPACES_REGEX.test(value)) {
    return translate('error.validation.password_contains_space');
  }

  return;
};

export const empty = (value?: unknown) => {
  if (isPrimativeEmpty(value)) {
    return translate('error.validation.empty');
  }

  return;
};

export const notEmail = (value?: string | null): string | undefined => {
  if (!EMAIL_REGEXP.test(String(value))) {
    return translate('error.validation.not_email');
  }

  return;
};

export const equal = (value1?: string | null, value2?: string | null) => {
  if (value1 !== value2) {
    return translate('error.validation.not_equal');
  }

  return;
};

export const notMobilePhone = (value?: string | null): string | undefined => {
  // Phone must start with 6 or 7, only numbers, 9 digits length, optionally with country code
  const MOBILE_PHONE_REGEX = /^(\+?420)?[67]([0-9]{8})$/;

  if (!MOBILE_PHONE_REGEX.test(String(value))) {
    return translate('error.validation.invalid_phone_number');
  }

  return;
};

export const minLength =
  (length: number, subject?: string) => (value?: string | null) => {
    if (typeof value === 'undefined' || String(value).trim().length < length) {
      return subject
        ? translate('error.validation.min_length_with_subject', {
            length,
            subject,
          })
        : translate('error.validation.min_length', { length });
    }

    return;
  };

export const maxLength =
  (length: number, subject?: string) => (value?: string | null) => {
    if (typeof value === 'undefined' || String(value).trim().length > length) {
      return subject
        ? translate('error.validation.max_length_with_subject', {
            length,
            subject,
          })
        : translate('error.validation.max_length', { length });
    }

    return;
  };

/** blocks completely the use of diacritics characters, used for password validation */
export const restrictDiacriticsChars = (
  value?: string | null,
): string | undefined => {
  const DIACRITICS_REGEX = /[À-ž]/;

  if (value && DIACRITICS_REGEX.test(value)) {
    return translate('error.validation.restrict_diacritics_chars');
  }

  return;
};

export const restrictSpecialChars = (value?: string) => {
  const SPECIAL_CHAR_REGEX = /[^À-ža-zA-Z0-9-_\s]/;

  if (value && SPECIAL_CHAR_REGEX.test(value)) {
    return translate('error.validation.restrict_special_chars');
  }

  return;
};

export const restrictFileSize =
  (maxSize: number, placeholder: string) => (file: File) => {
    if (file && file.size > maxSize) {
      return translate('error.validation.file_size_too_big', {
        placeholder,
      });
    }

    return;
  };

export const invalidFileFormatType = (validTypes: string[]) => (file: File) => {
  if (file && !validTypes.includes(file.type)) {
    return translate('error.validation.file_format_invalid');
  }

  return;
};

export const rangeValue =
(minValue: number, maxValue: number, validText?: string) => (value?: string | null) => {
  if (Number(value) > maxValue || Number(value) < minValue) {
    return validText ? validText : translate('error.validation.out_of_range');
  }

  return;
};

export const firstNumLarger = (firstValue: number, secondValue: number, validText?: string) => (value?: string | null) => {
  if(firstValue > secondValue) {
    return validText;
  }

  return;
}
